import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TablePagination } from '@/components/ui/TablePagination';
import { CardList } from '@/components/ui/CardList';
import { ActionMenu } from '@/components/ui/ActionMenu';
import { AuthButton } from '@/core/permissions/AuthButton';
import { Edit3, Eye, Lock, Trash2 } from 'lucide-react';
import { getRoleBadgeClasses } from '@/utils/role';
import { cn } from '@/utils/cn';
import { usePermission } from '@/core/permissions';
import type { Member, MemberStatus } from '../types/membersList.types';

interface MembersTableBlockProps {
  members: Member[];
  isLoading: boolean;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onEdit: (member: Member) => void;
  onDelete: (member: Member) => void;
  onToggleStatus: (member: Member) => void;
  onViewDetail: (member: Member) => void;
}

const statusConfig: Record<MemberStatus, { label: string; className: string }> = {
  active: { label: 'Hoạt động', className: 'text-emerald-600 bg-emerald-50' },
  locked: { label: 'Tạm khóa', className: 'text-slate-400 bg-slate-50' },
  disabled: { label: 'Vô hiệu', className: 'text-rose-600 bg-rose-50' },
  pending: { label: 'Đang chờ', className: 'text-amber-600 bg-amber-50' },
};

export function MembersTableBlock({
  members,
  isLoading,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onEdit,
  onDelete,
  onToggleStatus,
  onViewDetail,
}: MembersTableBlockProps) {
  const { can } = usePermission();
  const canUpdate = can('users', 'update');
  const canDelete = can('users', 'delete');
  
  const desktopColumns = [
    { key: 'info', label: 'Thành viên', className: 'pl-6' },
    { key: 'role', label: 'Vai trò', className: 'w-40' },
    { key: 'group', label: 'Nhóm', className: 'w-32' },
    { key: 'status', label: 'Trạng thái', className: 'w-32' },
    { key: 'actions', label: '', className: 'w-16 pr-6' },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Desktop Table View */}
      <div className="hidden rounded-md border border-slate-100 bg-white shadow-sm lg:block">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-slate-100 text-slate-500">
              {desktopColumns.map((col) => (
                <th key={col.key} className={cn('p-3 font-semibold text-xs', col.className)}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50 text-sm">
            <AnimatePresence mode="popLayout">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`skeleton-${i}`} className="animate-pulse">
                    <td colSpan={5} className="p-4"><div className="h-10 rounded-md bg-slate-50" /></td>
                  </tr>
                ))
              ) : members.length > 0 ? (
                members.map((member) => {
                  const status = statusConfig[member.status] || statusConfig.active;

                  return (
                    <motion.tr
                      key={member.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="group transition-colors hover:bg-slate-50/50 relative z-0 hover:z-10"
                    >
                      <td className="py-3 pl-6">
                        <div className="flex items-center gap-3">
                          <Avatar member={member} />
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-900">{member.fullName}</span>
                              {member.isMe && <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-700">Bạn</span>}
                            </div>
                            <span className="text-xs font-medium text-slate-400">{member.email}</span>
                          </div>
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {member.roles?.map((r, idx) => (
                            <span key={idx} className={getRoleBadgeClasses(r.code)}>
                              {r.name}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-3">
                        <span className="text-xs font-medium text-slate-500">{member.groupName}</span>
                      </td>
                      <td className="py-3">
                         <div className={cn('flex items-center gap-1.5', status.className)}>
                            <div className={cn('h-1.5 w-1.5 rounded-full', status.className.split(' ')[0].replace('text-', 'bg-'))} />
                            <span className="sm:text-sm text-xs font-medium">{status.label}</span>
                         </div>
                      </td>
                      <td className="py-3 pr-6 text-right">
                        <ActionMenu
                          align="right"
                          items={[
                            { label: 'Xem chi tiết', icon: Eye, onClick: () => onViewDetail(member) },
                            { label: 'Chỉnh sửa', icon: Edit3, onClick: () => onEdit(member), disabled: !canUpdate },
                            { label: member.status === 'locked' ? 'Mở khóa' : 'Khóa tài khoản', icon: Lock, onClick: () => onToggleStatus(member), disabled: !canUpdate },
                            { label: 'Xóa thành viên', icon: Trash2, destructive: true, onClick: () => onDelete(member), disabled: !canDelete },
                          ]}
                        />
                      </td>
                    </motion.tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-slate-400">
                    <i className="ri-user-search-line text-3xl mb-2" />
                    <p className="text-sm font-medium">Không tìm thấy thành viên phù hợp</p>
                  </td>
                </tr>
              )}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {/* Mobile View */}
      <div className="lg:hidden">
        <CardList
          items={members}
          isLoading={isLoading}
          renderCard={(member) => (
            <article key={member.id} className="rounded-md border border-slate-100 bg-white p-3 shadow-sm">
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar member={member} />
                    <div className="flex flex-col min-w-0">
                      <span className="truncate text-sm font-medium text-slate-900">{member.fullName}</span>
                      <span className="truncate text-xs text-slate-400">{member.email}</span>
                    </div>
                  </div>
                  <div className="shrink-0">
                    <div className={cn('rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap', statusConfig[member.status]?.className)}>
                      {statusConfig[member.status]?.label}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <div className="flex flex-wrap items-center gap-1.5 overflow-hidden">
                    {member.roles?.map((r, idx) => (
                      <span key={idx} className={getRoleBadgeClasses(r.code)}>
                        {r.name}
                      </span>
                    ))}
                    <span className="text-slate-300">/</span>
                    <span className="font-medium text-slate-500 truncate">{member.groupName}</span>
                  </div>
                  {member.isMe && <span className="shrink-0 rounded bg-emerald-50 px-1.5 py-0.5 font-medium text-emerald-700">Bạn</span>}
                </div>

                  <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-50">
                    <button
                      onClick={() => onViewDetail(member)}
                      className="flex flex-1 h-9 items-center justify-center gap-2 rounded-md border border-slate-100 bg-white text-xs font-medium text-slate-700 active:bg-slate-50"
                    >
                      <Eye className="h-4 w-4" />
                      <span>Xem</span>
                    </button>
                    <AuthButton
                      onClick={() => onEdit(member)}
                      module="users"
                      action="update"
                      behavior="disable"
                      permissionReason="Bạn không có quyền chỉnh sửa thành viên"
                      className="flex flex-1 h-9 items-center justify-center gap-2 rounded-md border border-slate-100 bg-white text-xs font-medium text-slate-700 active:bg-slate-50"
                    >
                      <Edit3 className="h-4 w-4" />
                      <span>Sửa</span>
                    </AuthButton>
                    <AuthButton
                      onClick={() => onDelete(member)}
                      module="users"
                      action="delete"
                      behavior="disable"
                      permissionReason="Bạn không có quyền xóa thành viên"
                      className="flex flex-1 h-9 items-center justify-center gap-2 rounded-md border border-red-50 bg-red-50 text-xs font-medium text-red-600 active:bg-red-100"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>Xóa</span>
                    </AuthButton>
                  </div>

              </div>
            </article>
          )}
        />
      </div>

      <TablePagination
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
      />
    </div>
  );
}

function Avatar({ member }: { member: Member }) {
  return (
    <div className="h-10 w-10 overflow-hidden rounded-full border-2 border-white bg-emerald-100 shadow-sm">
      {member.avatarPath ? (
        <img src={member.avatarPath} alt={member.fullName} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-xs font-medium text-emerald-700">
          {member.fullName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
        </div>
      )}
    </div>
  );
}
